/**
 * /operations/leave/carry-forward — Session 3a PR 7.
 *
 * Annual rollover wizard — captures expiring balances, applies
 * per-type carry-forward caps, expires the rest. Tashghil-owned per
 * Session 3a §4.6.
 *
 * AI surfaces (commandment-3 invariant):
 *   1. AiBriefPanel right rail (Tashghil narration)
 *   2. InlineAISuggestion cards (recommended-path + Mutaba'aa floor
 *      veto preview)
 *   3. TraceableValue wrapping to_carry_forward + to_expire + savings
 *      (≥3 wrappers)
 */

import { useQuery } from "@tanstack/react-query";
import { ArrowRightLeft, Calculator, ShieldAlert } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { ErrorPanel, Loading } from "../../../components/Loading";
import {
  InlineAISuggestion,
  PageHeader,
  TraceableValue,
} from "../../../components/ui";
import { session3aApi, type CarryForwardPreviewItem } from "../../../lib/session3a-api";
import { useLegalEntityGate } from "../../../lib/legalEntity";
import { AiBriefPanel } from "../../../shell/AiBriefPanel";
import { formatSar, severityChip, showAlertTrace } from "../../_session3aHelpers";

export function LeaveCarryForwardPage() {
  const { t } = useTranslation();
  const { legalEntityId, gate } = useLegalEntityGate();
  const [yearEnd, setYearEnd] = useState<string>(
    `${new Date().getFullYear()}-12-31`,
  );
  const [filter, setFilter] = useState<"all" | "has_expiring" | "exceeds_cap">(
    "all",
  );
  const [confirmText, setConfirmText] = useState("");
  const [showConfirm, setShowConfirm] = useState(false);

  const preview = useQuery({
    queryKey: ["leave-carry-forward", legalEntityId, yearEnd],
    queryFn: () =>
      session3aApi.previewCarryForward(legalEntityId ?? "", yearEnd),
    enabled: !!legalEntityId,
  });

  const summary = preview.data ?? {
    leave_year_end: yearEnd,
    total_employees_affected: 0,
    total_days_at_risk: 0,
    total_to_carry_forward: 0,
    total_to_expire: 0,
    total_encashment_cost_sar: 0,
    items: [] as CarryForwardPreviewItem[],
  };

  const filtered = useMemo(() => {
    const items = summary.items;
    if (filter === "has_expiring") return items.filter((r) => r.to_expire_days > 0);
    if (filter === "exceeds_cap")
      return items.filter((r) => r.current_balance_days > r.carry_forward_cap_days);
    return items;
  }, [summary.items, filter]);

  const stats = useMemo(() => {
    const floorBreaches = summary.items.filter((r) =>
      r.leave_type_code === "ANNUAL" &&
      r.current_balance_days - r.to_carry_forward_days < 21,
    ).length;
    return {
      total: summary.items.length,
      withExpiring: summary.items.filter((r) => r.to_expire_days > 0).length,
      floorBreaches,
      savings:
        summary.total_encashment_cost_sar -
        summary.total_to_carry_forward * 100, // sketch — UI surface only
    };
  }, [summary]);

  if (gate) return gate;

  return (
    <div className="grid grid-cols-[1fr_280px] gap-4">
      <div className="space-y-3">
        <PageHeader
          title={t(
            "operations.leave_carry_forward.title",
            "Year-End Carry-Forward",
          )}
          subtitle={t(
            "operations.leave_carry_forward.subtitle",
            "Annual rollover wizard — Tashghil recommends carry/encash/expire per employee, Mutaba'aa enforces the Article 109 floor.",
          )}
        />

        {/* Cycle Preview card */}
        <div className="rounded-2xl border border-border-subtle bg-gradient-to-br from-[#f5f9ff] to-[#f5f3ff] p-4">
          <div className="mb-3 flex items-center justify-between">
            <div>
              <p className="text-xs font-medium text-text-secondary">
                {t(
                  "operations.leave_carry_forward.year_end_label",
                  "Leave Year End",
                )}
              </p>
              <input
                type="date"
                value={yearEnd}
                onChange={(e) => setYearEnd(e.target.value)}
                className="mt-1 rounded-lg border border-border-subtle px-3 py-1 text-sm"
              />
            </div>
            <button
              type="button"
              onClick={() =>
                showAlertTrace(
                  t(
                    "operations.leave_carry_forward.ai_recommend_note",
                    "Tashghil walks every employee, picks the per-row recommended path (carry-forward / encash / expire) that maximises retention while honouring Article 109. Live wiring when /api/leave/carry-forward/run lands.",
                  ),
                )
              }
              className="inline-flex items-center gap-1.5 rounded-md bg-gradient-to-br from-[#00D3C2] to-[#A846BF] px-3 py-1.5 text-xs font-medium text-white hover:opacity-95"
            >
              ✦{" "}
              {t(
                "operations.leave_carry_forward.run_ai_path",
                "Run AI-recommended path",
              )}
            </button>
          </div>
          <div className="grid grid-cols-5 gap-2 text-xs">
            <div>
              <p className="text-text-tertiary">{t("operations.leave_carry_forward.kpi.affected", "Affected")}</p>
              <p className="font-semibold text-text-primary">
                {summary.total_employees_affected}
              </p>
            </div>
            <div>
              <p className="text-text-tertiary">{t("operations.leave_carry_forward.kpi.days_at_risk", "Days at risk")}</p>
              <TraceableValue
                onTraceClick={() =>
                  showAlertTrace(
                    t(
                      "operations.leave_carry_forward.trace.days_at_risk",
                      "days_at_risk = sum of balances exceeding next-cycle entitlement projection. Sourced from leave_balances + leave_type policies.",
                    ),
                  )
                }
              >
                <span className="font-semibold text-text-primary">
                  {summary.total_days_at_risk}
                </span>
              </TraceableValue>
            </div>
            <div>
              <p className="text-text-tertiary">{t("operations.leave_carry_forward.kpi.carry_forward", "Carry forward")}</p>
              <TraceableValue
                onTraceClick={() =>
                  showAlertTrace(
                    t(
                      "operations.leave_carry_forward.trace.carry_forward",
                      "to_carry_forward = min(at_risk, leave_type.carry_forward_cap_days) per employee.",
                    ),
                  )
                }
              >
                <span className="font-semibold text-emerald-700">
                  {summary.total_to_carry_forward}
                </span>
              </TraceableValue>
            </div>
            <div>
              <p className="text-text-tertiary">{t("operations.leave_carry_forward.kpi.expire", "Expire")}</p>
              <TraceableValue
                onTraceClick={() =>
                  showAlertTrace(
                    t(
                      "operations.leave_carry_forward.trace.expire",
                      "to_expire = at_risk − to_carry_forward. Article 109 protected balances are never expired.",
                    ),
                  )
                }
              >
                <span className="font-semibold text-amber-700">
                  {summary.total_to_expire}
                </span>
              </TraceableValue>
            </div>
            <div>
              <p className="text-text-tertiary">{t("operations.leave_carry_forward.kpi.alt_encash", "Alt. encash (SAR)")}</p>
              <span dir="ltr" className="font-semibold text-text-primary">
                {formatSar(summary.total_encashment_cost_sar)}
              </span>
            </div>
          </div>
        </div>

        <InlineAISuggestion
          agent="tashghil"
          icon={<Calculator className="h-4 w-4" />}
          severity="info"
          headline={t(
            "operations.leave_carry_forward.suggestion.recommend_headline",
            "Tashghil recommends a mixed path.",
          )}
          body={t(
            "operations.leave_carry_forward.suggestion.recommend_body",
            "Carrying 35% · encashing 50% · expiring 15% balances retention against SAR cost. Open the AI path to walk per-employee.",
          )}
          primaryAction={{
            label: t(
              "operations.leave_carry_forward.suggestion.apply_path",
              "Apply recommended path",
            ),
            onClick: () =>
              showAlertTrace(
                t(
                  "operations.leave_carry_forward.suggestion.apply_note",
                  "Per-employee recommendation table populates inline. Wiring lands with /api/leave/carry-forward/run.",
                ),
              ),
          }}
          suggestionKey={`carry-forward-recommend-${yearEnd}`}
        />
        {stats.floorBreaches > 0 && (
          <InlineAISuggestion
            agent="mutabaa"
            icon={<ShieldAlert className="h-4 w-4" />}
            severity="critical"
            headline={t(
              "operations.leave_carry_forward.suggestion.veto_headline",
              `Mutaba'aa veto: ${stats.floorBreaches} employees would drop below the Article 109 21-day floor.`,
            )}
            body={t(
              "operations.leave_carry_forward.suggestion.veto_body",
              "Those rows are auto-protected — carry-forward bumped to floor before expiry.",
            )}
            primaryAction={{
              label: t(
                "operations.leave_carry_forward.suggestion.see_protected",
                "See protected rows",
              ),
              onClick: () => setFilter("exceeds_cap"),
            }}
            suggestionKey={`carry-forward-floor-${yearEnd}`}
          />
        )}

        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex gap-1.5">
            <button
              type="button"
              onClick={() => setFilter("all")}
              className={`rounded-full border px-3 py-1 text-xs font-medium ${
                filter === "all"
                  ? "border-brand-blue bg-brand-blue/10 text-brand-deep"
                  : "border-border-subtle text-text-secondary hover:bg-surface-sunken"
              }`}
            >
              {t("operations.leave_carry_forward.filter.all", "All")}
            </button>
            <button
              type="button"
              onClick={() =>
                setFilter(filter === "has_expiring" ? "all" : "has_expiring")
              }
              className={`rounded-full border px-3 py-1 text-xs font-medium ${
                filter === "has_expiring"
                  ? "border-rose-300 bg-rose-50 text-rose-800"
                  : "border-border-subtle text-text-secondary hover:bg-surface-sunken"
              }`}
            >
              {t(
                "operations.leave_carry_forward.filter.has_expiring",
                "Has expiring days",
              )}
            </button>
            <button
              type="button"
              onClick={() =>
                setFilter(filter === "exceeds_cap" ? "all" : "exceeds_cap")
              }
              className={`rounded-full border px-3 py-1 text-xs font-medium ${
                filter === "exceeds_cap"
                  ? "border-amber-300 bg-amber-50 text-amber-800"
                  : "border-border-subtle text-text-secondary hover:bg-surface-sunken"
              }`}
            >
              {t(
                "operations.leave_carry_forward.filter.exceeds_cap",
                "Exceeds cap",
              )}
            </button>
          </div>
          <button
            type="button"
            onClick={() => setShowConfirm(true)}
            disabled={summary.items.length === 0}
            className="inline-flex items-center gap-1.5 rounded-md bg-gradient-to-br from-[#2260C9] to-[#A846BF] px-3 py-1.5 text-sm font-medium text-white hover:opacity-95 disabled:opacity-50"
          >
            <ArrowRightLeft className="h-4 w-4" />
            {t(
              "operations.leave_carry_forward.cta.run",
              "Run Carry-Forward",
            )}
          </button>
        </div>

        {preview.isLoading && <Loading />}
        {preview.isError && <ErrorPanel message={(preview.error as Error).message} />}

        {filtered.length > 0 && (
          <div className="overflow-hidden rounded-2xl border border-border-subtle bg-white">
            <table className="w-full text-sm">
              <thead className="bg-surface-sunken">
                <tr className="text-left text-[10px] font-semibold uppercase tracking-wide text-text-secondary">
                  <th className="px-3 py-2">{t("operations.leave_carry_forward.col.employee", "Employee")}</th>
                  <th className="px-3 py-2">{t("operations.leave_carry_forward.col.leave_type", "Leave type")}</th>
                  <th className="px-3 py-2">{t("operations.leave_carry_forward.col.current", "Current balance")}</th>
                  <th className="px-3 py-2">{t("operations.leave_carry_forward.col.cap", "Cap")}</th>
                  <th className="px-3 py-2">{t("operations.leave_carry_forward.col.to_carry", "Carry forward")}</th>
                  <th className="px-3 py-2">{t("operations.leave_carry_forward.col.to_expire", "Expire")}</th>
                  <th className="px-3 py-2">{t("operations.leave_carry_forward.col.recommended", "Recommended")}</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((row) => (
                  <tr
                    key={`${row.employee_id}-${row.leave_type_code}`}
                    className="border-t border-border-subtle hover:bg-surface-sunken/40"
                  >
                    <td className="px-3 py-2 text-text-secondary">
                      <span className="font-medium text-[#2260C9]">
                        {row.employee_name_en}
                      </span>
                      <div className="text-xs text-text-tertiary">{row.employee_code}</div>
                    </td>
                    <td className="px-3 py-2 text-text-secondary">{row.leave_type_name_en}</td>
                    <td className="px-3 py-2 text-text-secondary">{row.current_balance_days}</td>
                    <td className="px-3 py-2 text-text-secondary">{row.carry_forward_cap_days}</td>
                    <td className="px-3 py-2 text-emerald-700">{row.to_carry_forward_days}</td>
                    <td className="px-3 py-2">
                      {row.to_expire_days > 0 ? (
                        <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium ${severityChip("critical")}`}>
                          {row.to_expire_days}
                        </span>
                      ) : (
                        <span className="text-xs text-text-tertiary">—</span>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <span
                        className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium ${severityChip(
                          row.recommended === "carry_forward"
                            ? "info"
                            : row.recommended === "encash"
                              ? "info"
                              : "attention",
                        )}`}
                        title={row.recommended_rationale}
                      >
                        {row.recommended}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {!preview.isLoading && filtered.length === 0 && (
          <div className="rounded-2xl border border-dashed border-border-subtle p-6 text-center text-sm text-text-tertiary">
            {t(
              "operations.leave_carry_forward.empty",
              "No expiring balances detected — the cycle is healthy.",
            )}
          </div>
        )}

        {showConfirm && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
            <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl">
              <h2 className="text-lg font-semibold text-text-primary">
                {t(
                  "operations.leave_carry_forward.confirm.title",
                  "Confirm carry-forward run",
                )}
              </h2>
              <p className="mt-2 text-sm text-text-secondary">
                {t(
                  "operations.leave_carry_forward.confirm.body",
                  "Type RUN to confirm. This writes leave_balance_event rows for every affected employee. Cannot be reversed in batch.",
                )}
              </p>
              <input
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder="RUN"
                className="mt-3 w-full rounded-lg border border-border-subtle px-3 py-2 font-mono"
              />
              <div className="mt-3 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setShowConfirm(false)}
                  className="rounded-lg border border-border-subtle px-4 py-2 text-sm font-medium text-text-secondary hover:bg-surface-sunken"
                >
                  {t("common.cancel", "Cancel")}
                </button>
                <button
                  type="button"
                  disabled={confirmText !== "RUN"}
                  onClick={() => {
                    showAlertTrace(
                      t(
                        "operations.leave_carry_forward.confirm.executed",
                        "Carry-forward will execute against /api/leave/carry-forward/run with the previewed deltas once the endpoint lands.",
                      ),
                    );
                    setShowConfirm(false);
                    setConfirmText("");
                  }}
                  className="rounded-md bg-gradient-to-br from-[#2260C9] to-[#A846BF] px-3 py-1.5 text-sm font-medium text-white hover:opacity-95 disabled:opacity-50"
                >
                  {t("operations.leave_carry_forward.confirm.execute", "Execute")}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
      <aside className="rounded-2xl border border-border-subtle bg-white">
        <AiBriefPanel
          agent="tashghil"
          liveContext={{
            total: summary.total_employees_affected,
            attention: stats.withExpiring,
            critical: stats.floorBreaches,
            top_kind: t(
              "operations.leave_carry_forward.title",
              "Year-End Carry-Forward",
            ),
            window_label: `year ends ${yearEnd}`,
          }}
        />
      </aside>
    </div>
  );
}
